/**
 * Submission Pack header band (Specs/submission-pack-spec.md, Task B6).
 *
 * Follows the design system's sticky sub-header (metadata strip) pattern: one
 * compact row, `-mx-6 -mt-4` to break out of the tab container's padding,
 * opaque `bg-background` so the list scrolls under it. No bottom hairline —
 * Kapil dropped it here (2026-09-30); the title band doesn't need a divider.
 *
 * Export opens `PackExportDialog`; the run itself lives in the store, so this
 * button carries the progress state after the dialog has closed. Read-only
 * viewers get no button at all — the pack is not theirs to ship.
 */

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { AlertTriangle, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { PackExportDialog } from '@/components/pack/PackExportDialog'
import { daysUntilDeadline, isDeadlinePast } from '@/lib/bid-deadline'
import { dominantLodgementMethod, formatDeadline } from '@/lib/pack-header'
import { PACK_PREPARATION_COPY } from '@/lib/pack-readiness'
import { cn } from '@/lib/utils'
import { useSubmissionPackStore } from '@/stores/submission-pack-store'
import type { PackItem, SubmissionPackBlockers, SubmissionPackRollup } from '@/lib/api'

interface PackHeaderProps {
  bidId: string
  rollup: SubmissionPackRollup
  blockers: SubmissionPackBlockers
  /** ISO closing date, or null when the tender never gave one. */
  deadline: string | null
  /** Whether `deadline` is an exact closing time; null = not recorded (older bids). */
  deadlineHasTime?: boolean | null
  /** Live items — only read for the shared lodgement method. */
  items?: PackItem[]
  readOnly?: boolean
  /** Closes the dialog and scrolls the blockers band into view. */
  onReviewBlockers: () => void
  /** Reference point for the countdown; pass explicitly to keep tests fixed. */
  now?: Date
}

/**
 * "Closes in 4 days" / "Closes today" / "Closed 2 days ago", or null when the
 * deadline is missing or unparseable — in which case the whole countdown is
 * hidden rather than rendered as an empty slot. A closing time that passed
 * earlier today reads "Closed today".
 */
export function deadlineCountdown(
  deadline: string | null,
  now: Date = new Date(),
  hasTime: boolean | null = null
): string | null {
  const days = daysUntilDeadline(deadline, now)
  if (days === null) return null
  if (days === 0) return isDeadlinePast({ deadline, deadlineHasTime: hasTime }, now) ? 'Closed today' : 'Closes today'
  if (days > 0) return `Closes in ${days} ${days === 1 ? 'day' : 'days'}`
  const past = Math.abs(days)
  return `Closed ${past} ${past === 1 ? 'day' : 'days'} ago`
}

export function PackHeader({
  bidId,
  rollup,
  blockers,
  deadline,
  deadlineHasTime = null,
  items = [],
  readOnly = false,
  onReviewBlockers,
  now = new Date(),
}: PackHeaderProps) {
  const isExporting = useSubmissionPackStore((s) => s.isExporting)
  const [exportOpen, setExportOpen] = useState(false)
  const countdown = deadlineCountdown(deadline, now, deadlineHasTime)
  // Null-safe: a tender with no deadline is not closed. A closing time is
  // closed from that moment; a date once its day has ended.
  const isClosed = isDeadlinePast({ deadline, deadlineHasTime }, now)
  const absolute = deadline ? formatDeadline(deadline, deadlineHasTime) : ''
  const method = dominantLodgementMethod(items)
  // `total` already excludes not_required — it is not work, so it must not
  // dilute the percentage either. `rollup.ready` counts PREPARED items (R0-B).
  const percent = rollup.total > 0 ? Math.round((rollup.ready / rollup.total) * 100) : 0
  const inProgressPercent =
    rollup.total > 0 ? Math.round((rollup.inProgress / rollup.total) * 100) : 0

  return (
    // flex-wrap: below ~900px of content the readiness block and Export drop to
    // a second line instead of colliding with the deadline text.
    <div className="sticky top-0 z-10 -mx-6 -mt-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 bg-background px-6 py-3">
      <div className="flex min-w-0 flex-1 basis-72 flex-wrap items-baseline gap-x-3 gap-y-0.5">
        <h2 className="whitespace-nowrap text-sm font-medium text-foreground">
          Submission Pack
        </h2>
        {(countdown || method) && (
          <p className="text-meta min-w-0 max-w-full truncate">
            {countdown && (
              <Link
                to={`/app/bids/${bidId}/analysis?view=key_dates`}
                // Plain inline, not inline-flex: an atomic inline box would be
                // clipped without the ellipsis `truncate` promises.
                className={cn(
                  'hover:underline',
                  isClosed ? 'text-[hsl(var(--status-warning))]' : 'text-[hsl(var(--link))]'
                )}
              >
                {isClosed && (
                  <AlertTriangle aria-hidden className="mr-1 inline h-3.5 w-3.5 align-[-2px]" />
                )}
                {countdown}
                {absolute && ` · ${absolute}`}
              </Link>
            )}
            {method && (
              <span>
                {countdown && ' · '}
                Lodge via {method}
              </span>
            )}
          </p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-3">
        {rollup.total > 0 && (
          <div className="w-48">
            <div className="text-meta flex items-baseline justify-between gap-2 whitespace-nowrap">
              <span>
                <span className="font-medium text-foreground">
                  {rollup.ready} of {rollup.total}
                </span>{' '}
                {PACK_PREPARATION_COPY.headerSuffix}
              </span>
              {rollup.inProgress > 0 && <span>{rollup.inProgress} in progress</span>}
            </div>
            <div
              role="progressbar"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
              aria-label={PACK_PREPARATION_COPY.progressAriaLabel(percent)}
              className="mt-1 flex h-1.5 w-48 overflow-hidden rounded-full bg-muted"
            >
              {/* Inline widths: the only dynamic values here; Tailwind cannot
                  generate arbitrary percentages at runtime. */}
              <div
                className="h-full bg-[hsl(var(--status-success))]"
                style={{ width: `${percent}%` }}
              />
              <div
                className="h-full bg-[hsl(var(--status-warning))]"
                style={{ width: `${inProgressPercent}%` }}
              />
            </div>
          </div>
        )}
        {!readOnly && (
          <>
            {/* A working copy from the current text, labelled as a draft (R3):
                the kept files to lodge are under "Files to send". Outline, not
                filled: Mark as submitted is the page's one primary action. */}
            <Button variant="outline" size="sm" onClick={() => setExportOpen(true)} disabled={isExporting}>
              {isExporting && <Loader2 aria-hidden className="mr-2 h-4 w-4 animate-spin" />}
              {isExporting ? 'Exporting…' : 'Export draft pack'}
            </Button>
            <PackExportDialog
              open={exportOpen}
              onOpenChange={setExportOpen}
              blockers={blockers}
              onReviewBlockers={() => {
                setExportOpen(false)
                onReviewBlockers()
              }}
            />
          </>
        )}
      </div>

      {/* R0-B: the roll-up counts preparation (drafted or linked), never a
          review or approval. Its own full-width line so the strip's single row
          stays intact; right-aligned under the roll-up it explains. */}
      {rollup.total > 0 && (
        <p className="text-meta basis-full sm:text-right">{PACK_PREPARATION_COPY.meaningNote}</p>
      )}
    </div>
  )
}
